import { Container, Title, Text, Paper, Box, Group, useMantineTheme, Image, SimpleGrid, Stack } from '@mantine/core';
import { IconHeartFilled } from '@tabler/icons-react';
import { ClinicMap } from '../components/ClinicMap';
import { SEOHead } from '../components/SEOHead';

export function AboutPage() {
  const theme = useMantineTheme();
  
  return (
    <>
      <SEOHead 
        title="Om Lykke Terapien i Horsens"
        description="Læs om Pernille Lykke Nielsen og hendes holistiske tilgang til akupunktur i Horsens. Personlig behandling med nærvær og respekt."
        canonical="/om-mig"
      />
      <Container size="md" py="xl" pt={50} mx="auto" className="about-page-container">
      <Paper 
        p="xl" 
        radius="md" 
        withBorder 
        shadow="sm"
        style={{
          background: 'rgba(255, 255, 255, 0.8)',
          borderColor: theme.colors.gold[2]
        }}
        className="about-main-paper"
      >
        <Group mb="lg" align="center" className="about-header">
          <IconHeartFilled size={24} style={{ color: theme.colors.gold[6] }} />
          <Title order={2} c={theme.colors.gold[7]} className="about-main-title">
            Om Lykke Terapien
          </Title>
        </Group>
        
        <Text mb="md" lh={1.6} className="about-text">
          Mit navn er Pernille Lykke Nielsen, og jeg driver en akupunkturklinik i rolige og naturskønne omgivelser nær Horsens. Min faglige baggrund spænder bredt – jeg er uddannet social- og sundhedsassistent samt ergoterapeut, og derudover er jeg certificeret NADA-udøver. Jeg er uddannet akupunktør fra Danmarks Akupunktur Uddannelse i Odder, hvor jeg har studeret klassisk kinesisk medicin.
        </Text>
        <Text mb="md" lh={1.6} className="about-text">
          Jeg er RAB-registreret akupunktør, hvor jeg arbejder med en tilgang, hvor kroppen ses som en helhed, og hvor behandlingen ikke kun fokuserer på lokale symptomer, men også på de bagvedliggende årsager til ubalancen. Jeg tror på, at vejen til velvære går gennem en helhedsorienteret tilgang, og at kroppen ofte fortæller os mere, end vi selv er opmærksomme på.
        </Text>
        <Text mb="md" lh={1.6} className="about-text">
          I klinikken møder jeg dig med ro, nærvær og respekt for netop din historie og de erfaringer, du kommer med. Det er vigtigt for mig at skabe et trygt rum, hvor du føler dig set og mødt som det menneske, du er.
        </Text>

        <Text mb="md" lh={1.6} className="about-text">
          Jeg tilbyder et støttende og fortroligt miljø for dig, der søger personlig vækst og forbedret fysisk og mental velvære. Min tilgang er baseret på evidensbaserede praksisser, skræddersyet til at imødekomme dine unikke behov.
        </Text>

        <Text mb="xl" lh={1.6} className="about-text">
          <strong>Tilskud til behandlinger:</strong> Jeg er RAB-registreret alternativ behandler, hvilket også betyder for dig, at du kan få tilskud til dine akupunkturbehandlinger gennem Sygeforsikringen "danmark".
        </Text>
        
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" my="xl">
          <Image 
            src="/pernille.png" 
            alt="Pernille Lykke Nielsen"
            radius="sm"
            fit="cover"
            mah={400}
            className="about-pernille-image"
          />
          <Image 
            src="/klinik.png" 
            alt="Klinik Lykke Terapien"
            radius="sm"
            fit="cover"
            mah={400}
            className="about-clinic-image"
          />
        </SimpleGrid>
        
        {/* Contact Section */}
        <Paper 
          p="xl" 
          radius="md" 
          withBorder 
          shadow="sm"
          mt="xl"
          style={{
            background: 'rgba(255, 248, 220, 0.3)',
            borderColor: theme.colors.gold[3]
          }}
          className="about-contact-paper"
        >
          <Box mb="sm">
            <Title order={3} c={theme.colors.gold[7]} className="about-contact-title">
              Kontakt
            </Title>
          </Box>
          
          <Text mb="lg" lh={1.6} className="about-contact-text">
            Du er velkommen til at kontakte mig ved spørgsmål eller ved interesse i behandlingsformerne.
          </Text>
          
          <Stack gap="xs" mb="lg" className="about-contact-details">
            <Text fw={500} className="about-contact-name">Lykke Terapien</Text>
            <Text className="about-contact-info">V/ Pernille Lykke Nielsen</Text>
            <Text className="about-contact-info">Stampemøllevej 21, 8700 Horsens</Text>
            <Text className="about-contact-info">Tlf: +45 60 80 37 35</Text>
            <Text className="about-contact-info">CVR 45461378</Text>
          </Stack>
          
          <Group gap="xs" className="about-parking-info">
            <Text>🚘</Text>
            <Text fw={500}>Parkering:</Text>
            <Text>Der er parkering lige uden for klinikken.</Text>
          </Group>
        </Paper>
        
        {/* needleTherapy.jpg and øre.png in a grid */}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg" my="xl" className="about-images-grid">
          <Box style={{ display: 'flex', justifyContent: 'center' }} className="about-image-container">
            <Image 
              src="/needleTherapy.jpg" 
              alt="Akupunktur behandling"
              radius="sm"
              fit="cover"
              h={300}
              w={300}
              maw={300}
              style={{ 
                objectPosition: 'center',
                aspectRatio: '1/1'
              }}
              className="about-treatment-image"
            />
          </Box>
          <Box style={{ display: 'flex', justifyContent: 'center' }} className="about-image-container">
            <Image 
              src="/øre.png" 
              alt="Øreakupunktur punkter"
              radius="sm"
              fit="cover"
              h={300}
              w={300}
              maw={300}
              style={{ 
                objectPosition: 'center',
                aspectRatio: '1/1'
              }}
              className="about-treatment-image"
            />
          </Box>
        </SimpleGrid>
        
        <Text mt="lg" fs="italic" c="dimmed" ta="center" lh={1.6} className="about-footer-text">
          Jeg ser frem til at støtte dig på din vej mod velvære.
        </Text>
      </Paper>
      
      {/* Clinic Location Map */}
      <Box mt="xl" className="about-map-container">
        <ClinicMap />
      </Box>

      <style>{`
        /* Mobile-first responsive design */
        @media (max-width: 768px) {
          .about-page-container {
            padding: 1rem !important;
            padding-top: 60px !important;
          }
          
          .about-main-paper {
            padding: 1rem !important;
          }
          
          .about-header {
            margin-bottom: 1rem !important;
            flex-direction: column !important;
            text-align: center !important;
          }
          
          .about-main-title {
            font-size: 1.5rem !important;
            text-align: center !important;
          }
          
          .about-text {
            font-size: 0.9rem !important;
            line-height: 1.5 !important;
            margin-bottom: 1rem !important;
            text-align: left !important;
          }
          
          .about-clinic-image,
          .about-pernille-image {
            margin: 1.5rem 0 !important;
            max-height: 250px !important;
          }
          
          .about-contact-paper {
            padding: 1rem !important;
            margin-top: 1.5rem !important;
          }
          
          .about-contact-title {
            font-size: 1.25rem !important;
            margin-bottom: 0.5rem !important;
          }
          
          .about-contact-text {
            font-size: 0.9rem !important;
            line-height: 1.5 !important;
            margin-bottom: 1rem !important;
          }
          
          .about-contact-details {
            gap: 0.5rem !important;
            margin-bottom: 1rem !important;
          }
          
          .about-contact-name {
            font-size: 0.95rem !important;
          }
          
          .about-contact-info {
            font-size: 0.875rem !important;
          }
          
          .about-parking-info {
            gap: 0.5rem !important;
            flex-wrap: wrap !important;
          }
          
          .about-parking-info .mantine-Text-root {
            font-size: 0.875rem !important;
          }
          
          .about-images-grid {
            margin: 1.5rem 0 !important;
            gap: 1rem !important;
          }
          
          .about-image-container {
            justify-content: center !important;
          }
          
          .about-treatment-image {
            height: 200px !important;
            width: 200px !important;
            max-width: 200px !important;
          }
          
          .about-footer-text {
            font-size: 0.875rem !important;
            margin-top: 1rem !important;
          }
          
          .about-map-container {
            margin-top: 1.5rem !important;
          }
        }
        
        /* Tablet improvements */
        @media (min-width: 769px) and (max-width: 1024px) {
          .about-page-container {
            padding: 1.5rem !important;
            padding-top: 70px !important;
          }
          
          .about-main-paper {
            padding: 2rem !important;
          }
          
          .about-main-title {
            font-size: 1.75rem !important;
          }
          
          .about-text {
            font-size: 1rem !important;
          }
          
          .about-clinic-image,
          .about-pernille-image {
            max-height: 350px !important;
          }
          
          .about-contact-paper {
            padding: 1.5rem !important;
          }
          
          .about-treatment-image {
            height: 250px !important;
            width: 250px !important;
            max-width: 250px !important;
          }
        }
        
        /* Enhanced touch interactions */
        @media (max-width: 768px) {
          .about-treatment-image {
            transition: all 0.3s ease !important;
          }
          
          .about-treatment-image:hover {
            transform: scale(1.02) !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
          }
        }
        
        /* Better visual hierarchy */
        @media (max-width: 768px) {
          .about-header .mantine-ThemeIcon-root {
            width: 32px !important;
            height: 32px !important;
          }
        }
        
        /* Improved spacing */
        @media (max-width: 768px) {
          .about-contact-details .mantine-Text-root {
            padding: 0.25rem 0 !important;
          }
        }
        
        /* Better readability */
        @media (max-width: 768px) {
          .about-text {
            text-align: left !important;
            hyphens: none !important;
          }
        }
        
        /* Enhanced focus states */
        @media (max-width: 768px) {
          .about-treatment-image:focus {
            outline: 2px solid ${theme.colors.blue[6]} !important;
            outline-offset: 2px !important;
          }
        }
        
        /* Improved container spacing */
        @media (max-width: 768px) {
          .about-page-container .mantine-Container-root {
            padding-left: 1rem !important;
            padding-right: 1rem !important;
          }
        }
        
        /* Better image grid on mobile */
        @media (max-width: 768px) {
          .about-images-grid .mantine-SimpleGrid-root {
            grid-template-columns: 1fr !important;
          }
        }
        
        /* Enhanced paper styling */
        @media (max-width: 768px) {
          .about-main-paper,
          .about-contact-paper {
            border-radius: 8px !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
          }
        }
      `}</style>
      </Container>
    </>
  );
} 